/**
 * A traditional-style playing card rendered as an inline SVG.
 *
 * Bundled-in so we don't ship a separate asset file. Cards scale crisply,
 * support dimming/selection/hover, and highlight point cards (5/10/K) with
 * a gold stripe on the LEFT edge — visible even when cards overlap in a
 * fanned hand (where the right edge is hidden by the next card).
 */

import type { ReactNode } from "react";
import type { CardId } from "../../../shared/games/shengji.js";
import { useCardBack, useCardStyle, type CardStyle } from "./cardThemes.js";

/**
 * A `CardDecorator` is an arbitrary SVG fragment rendered on top of the
 * card body, inside the same 100×140 viewBox the card itself uses. The
 * decorator is provided per-card by the game module (Sheng Ji uses a
 * gold stripe to mark point cards; Hearts marks the queen of spades and
 * any heart). This keeps PlayingCard game-agnostic.
 */
export type CardDecorator = ReactNode;

interface PlayingCardProps {
  card: CardId | null;          // null = face-down
  width?: number;
  selected?: boolean;
  dimmed?: boolean;             // unplayable / not selectable
  selectable?: boolean;
  onClick?: () => void;
  faceDown?: boolean;
  /** Optional game-specific overlay (e.g. point-card stripe). */
  decorator?: CardDecorator;
  className?: string;
  style?: React.CSSProperties;
}

const SUIT_GLYPH: Record<string, string> = {
  C: "♣",
  D: "♦",
  H: "♥",
  S: "♠",
  J: "*",
};

const RANK_LABEL: Record<string, string> = {
  "2": "2", "3": "3", "4": "4", "5": "5", "6": "6", "7": "7",
  "8": "8", "9": "9", "T": "10", "J": "J", "Q": "Q", "K": "K", "A": "A",
};

function parseCard(id: CardId): { suit: string; rank: string; isJoker: boolean; jokerKind?: "small" | "big" } {
  if (id === "SJ") return { suit: "J", rank: "sj", isJoker: true, jokerKind: "small" };
  if (id === "BJ") return { suit: "J", rank: "bj", isJoker: true, jokerKind: "big" };
  // Cabo uses a single "JK" token for jokers (no small/big distinction).
  if (id === "JK") return { suit: "J", rank: "jk", isJoker: true, jokerKind: "big" };
  return { suit: id[1]!, rank: id[0]!, isJoker: false };
}

function pipPositions(rank: string): { x: number; y: number; flip?: boolean }[] {
  const C = { x: 0.5, y: 0.5 };
  const TL = { x: 0.30, y: 0.20 }, TR = { x: 0.70, y: 0.20 };
  const ML = { x: 0.30, y: 0.50 }, MR = { x: 0.70, y: 0.50 };
  const BL = { x: 0.30, y: 0.80, flip: true }, BR = { x: 0.70, y: 0.80, flip: true };
  const TC = { x: 0.50, y: 0.20 }, BC = { x: 0.50, y: 0.80, flip: true };
  const MTC = { x: 0.50, y: 0.35 }, MBC = { x: 0.50, y: 0.65, flip: true };
  switch (rank) {
    case "2": return [TC, BC];
    case "3": return [TC, C, BC];
    case "4": return [TL, TR, BL, BR];
    case "5": return [TL, TR, C, BL, BR];
    case "6": return [TL, TR, ML, MR, BL, BR];
    case "7": return [TL, TR, MTC, ML, MR, BL, BR];
    case "8": return [TL, TR, MTC, ML, MR, MBC, BL, BR];
    case "9": return [
      // 9-pip layout: 4 pips top half (2×2), 1 in centre, 4 bottom half (2×2 flipped).
      { x: 0.30, y: 0.20 }, { x: 0.70, y: 0.20 },
      { x: 0.30, y: 0.36 }, { x: 0.70, y: 0.36 },
      { x: 0.50, y: 0.50 },
      { x: 0.30, y: 0.64, flip: true }, { x: 0.70, y: 0.64, flip: true },
      { x: 0.30, y: 0.80, flip: true }, { x: 0.70, y: 0.80, flip: true },
    ];
    case "T": return [
      // 10-pip layout: 4-2-4 standard (top 2×2, middle pair, bottom 2×2 flipped).
      { x: 0.30, y: 0.20 }, { x: 0.70, y: 0.20 },
      { x: 0.30, y: 0.35 }, { x: 0.70, y: 0.35 },
      { x: 0.30, y: 0.50 }, { x: 0.70, y: 0.50 },
      { x: 0.30, y: 0.65, flip: true }, { x: 0.70, y: 0.65, flip: true },
      { x: 0.30, y: 0.80, flip: true }, { x: 0.70, y: 0.80, flip: true },
    ];
    default: return [];
  }
}

export function PlayingCard(props: PlayingCardProps) {
  const { card, width = 96, selected, dimmed, selectable, onClick, faceDown, decorator, className, style } = props;
  const cardStyle = useCardStyle();
  const back = useCardBack();
  const height = Math.round(width * 1.4);
  const radius = Math.round(width * 0.08);
  // Per-style border stroke multiplier — "bold" gets a heavier hairline.
  const borderStroke = Math.max(1, Math.round(width * 0.025 * cardStyle.borderStrokeMul));

  // Composition of CSS classes — actual select/hover/dim styling lives in
  // global.css so :hover can lift unselected cards without fighting inline
  // styles.
  const classes = [
    "playing-card",
    selectable ? "selectable" : "",
    selected ? "selected" : "",
    dimmed ? "dimmed" : "",
    className ?? "",
  ].filter(Boolean).join(" ");

  const baseStyle: React.CSSProperties = {
    width,
    height,
    borderRadius: radius,
    ...style,
  };

  if (faceDown || card === null) {
    return (
      <div className={classes} style={baseStyle}>
        <svg viewBox="0 0 100 140" width={width} height={height}>
          <rect x={1} y={1} width={98} height={138} rx={8} ry={8}
                fill={back.primary} stroke="#fff" strokeWidth={borderStroke} />
          <g fill={back.secondary}>
            <rect x={6} y={6} width={88} height={128} rx={5} ry={5} fill={back.secondary} />
          </g>
          <g stroke={back.pattern} strokeWidth={1} fill="none">
            {Array.from({ length: 8 }).map((_, i) => (
              <line key={i} x1={6 + i * 11} y1={6} x2={6} y2={6 + i * 11} />
            ))}
            {Array.from({ length: 8 }).map((_, i) => (
              <line key={`b${i}`} x1={94 - i * 11} y1={134} x2={94} y2={134 - i * 11} />
            ))}
          </g>
          <circle cx={50} cy={70} r={18} fill={back.medallion} stroke={back.medallionInk} strokeWidth={1.5} />
          <text x={50} y={76} fontSize={22} textAnchor="middle" fill={back.medallionInk}
                fontFamily="'Cinzel', 'Georgia', serif" fontWeight={700}>T</text>
        </svg>
      </div>
    );
  }

  const parsed = parseCard(card);

  return (
    <div
      className={classes}
      style={baseStyle}
      onClick={selectable && !dimmed ? onClick : undefined}
      role={selectable ? "button" : undefined}
      aria-disabled={dimmed}
    >
      <svg viewBox="0 0 100 140" width={width} height={height}>
        <rect x={1} y={1} width={98} height={138} rx={8} ry={8}
              fill={cardStyle.face} stroke={cardStyle.border} strokeWidth={borderStroke} />
        {/* Game-specific overlay (e.g. Sheng Ji point stripe, Hearts marker). */}
        {decorator}
        {parsed.isJoker
          ? renderJoker(parsed.jokerKind === "big", cardStyle)
          : renderFace(parsed.suit, parsed.rank, cardStyle)}
      </svg>
    </div>
  );
}

function renderJoker(isBig: boolean, style: CardStyle) {
  // Big joker takes the red-suit color, small joker takes the default
  // ink. In `mono` both collapse to a single ink color (intentional —
  // the style is meant to read flat).
  const color = isBig ? style.suit.H : style.ink;
  return (
    <g fill={color}>
      {/* Top-left corner */}
      <text x={11} y={22} fontSize={15} textAnchor="middle"
            fontFamily="'Inter', sans-serif" fontWeight={700}>★</text>
      <text x={11} y={36} fontSize={10} textAnchor="middle"
            fontFamily="'Inter', sans-serif" letterSpacing={0.5}>JKR</text>

      {/* Center */}
      <text x={50} y={96} fontSize={66} textAnchor="middle"
            fontFamily="'Inter', sans-serif" fontWeight={400}>★</text>

      {/*
       * Bottom-right corner: mirror of the top-left, achieved via a
       * translate-then-rotate transform. Inside this group the inner
       * coordinate (10,20)/(10,32) maps to (90,120)/(90,108) in the
       * viewBox — same as the top-left positions, kept symmetric and
       * fully inside the 140-tall card.
       */}
      <g transform="translate(100, 140) rotate(180)">
        <text x={11} y={22} fontSize={15} textAnchor="middle"
              fontFamily="'Inter', sans-serif" fontWeight={700}>★</text>
        <text x={11} y={36} fontSize={10} textAnchor="middle"
              fontFamily="'Inter', sans-serif" letterSpacing={0.5}>JKR</text>
      </g>
    </g>
  );
}

function renderFace(suit: string, rank: string, style: CardStyle) {
  const color =
    suit === "C" ? style.suit.C :
    suit === "D" ? style.suit.D :
    suit === "H" ? style.suit.H :
    suit === "S" ? style.suit.S :
    style.ink;
  const glyph = SUIT_GLYPH[suit] ?? "?";
  const label = RANK_LABEL[rank] ?? rank;
  const isFace = rank === "J" || rank === "Q" || rank === "K";
  const isAce = rank === "A";

  return (
    <g>
      {/* Top-left corner */}
      <text x={11} y={22} fontSize={style.cornerRankSize} textAnchor="middle" fill={color}
            fontFamily="'Inter', 'Helvetica Neue', sans-serif" fontWeight={style.cornerWeight}>
        {label}
      </text>
      <text x={11} y={36} fontSize={style.cornerSuitSize} textAnchor="middle" fill={color}>{glyph}</text>

      {/* Bottom-right corner: mirror of top-left. */}
      <g transform="translate(100, 140) rotate(180)">
        <text x={11} y={22} fontSize={style.cornerRankSize} textAnchor="middle" fill={color}
              fontFamily="'Inter', 'Helvetica Neue', sans-serif" fontWeight={style.cornerWeight}>
          {label}
        </text>
        <text x={11} y={36} fontSize={style.cornerSuitSize} textAnchor="middle" fill={color}>{glyph}</text>
      </g>

      {/* Body */}
      {isFace ? (
        <g>
          <rect x={22} y={42} width={56} height={62} rx={4} fill={style.faceBox}
                stroke={color} strokeWidth={1.4} />
          <text x={50} y={76} fontSize={32} textAnchor="middle" fill={color}
                fontFamily="'Cinzel', 'Georgia', serif" fontWeight={style.cornerWeight}>{label}</text>
          <text x={50} y={96} fontSize={16} textAnchor="middle" fill={color}>{glyph}</text>
        </g>
      ) : isAce ? (
        <text x={50} y={86} fontSize={52} textAnchor="middle" fill={color}>{glyph}</text>
      ) : (
        <g>
          {pipPositions(rank).map((p, i) => (
            <text
              key={i}
              x={p.x * 100}
              y={p.y * 140 + 5}
              fontSize={style.pipSize}
              textAnchor="middle"
              fill={color}
              transform={p.flip ? `rotate(180, ${p.x * 100}, ${p.y * 140})` : undefined}
            >
              {glyph}
            </text>
          ))}
        </g>
      )}
    </g>
  );
}
